/*
 * CategoryGridPlus — Compact parent/child category grid with inline row expansion
 *
 * Usage example:
 *   import CategoryGridPlus from '../components/categories/CategoryGridPlus';
 *
 *   <CategoryGridPlus
 *     categories={categories}   // full flat or nested list from context
 *     showAll                   // enable expand/collapse for subcategories (default false)
 *     columnsPerRow={3}         // columns on desktop; forced to 1 on mobile < 640px
 *   />
 *
 * Behavior:
 *   - Renders only parent categories (parentId == null/undefined) in the main grid
 *   - When showAll is true, each parent with children gets an expand/collapse button
 *   - Expanded children are injected immediately below the expanded parent's row,
 *     NOT appended at the end of the list
 *   - Row injection: rowIndex = Math.floor(cardIndex / columnsPerRow)
 *     → after each row, check if any card in that row is expanded → render children below
 *   - Accordion behavior: only one parent open at a time
 *   - Mobile (< 640px): always 1 column regardless of columnsPerRow
 *   - Group visual: box-shadow border + subtle bg tint wraps parent row + children together
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Category as CategoryIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useCategoryName, useCategoryDescription } from '../../utils/categoryTranslation';

// ─── Constants ────────────────────────────────────────────────────────────────

const API_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:5000/api/v1';

const ACCENT = '#2563eb';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildImageUrl = (rawUrl) => {
  if (!rawUrl) return null;
  if (rawUrl.startsWith('http')) return rawUrl;
  return `${API_URL}/${rawUrl}`;
};

// ─── ParentCard ───────────────────────────────────────────────────────────────

const ParentCard = ({ category, isExpanded, onToggle, showExpand }) => {
  const { t } = useTranslation('common');
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  const imageUrl = buildImageUrl(category.imageUrl || category.image);
  const hasChildren = Boolean(category.children?.length);

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1.5px solid ${isExpanded ? ACCENT : 'rgba(15,23,42,0.09)'}`,
        bgcolor: isExpanded ? `${ACCENT}08` : 'background.paper',
        boxShadow: isExpanded
          ? `0 4px 18px ${ACCENT}22`
          : '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'border-color .2s, box-shadow .2s, background-color .2s',
      }}
    >
      {/* Image zone — 16:9, object-fit cover, placeholder on missing image */}
      <Box
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          flexShrink: 0,
          overflow: 'hidden',
          bgcolor: `${ACCENT}0d`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={getCategoryName(category)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <CategoryIcon sx={{ fontSize: 52, color: `${ACCENT}55` }} />
        )}
      </Box>

      {/* Bottom panel: name + description + expand button */}
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="subtitle1"
          title={getCategoryName(category)}
          sx={{
            fontWeight: 700,
            color: '#0f172a',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getCategoryName(category)}
        </Typography>

        {category.description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              flex: 1,
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {getCategoryDescription(category)}
          </Typography>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        {/* Expand/collapse toggle — only shown when showAll and category has children */}
        {showExpand && hasChildren && (
          <Button
            variant={isExpanded ? 'outlined' : 'contained'}
            fullWidth
            size="medium"
            onClick={onToggle}
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              mt: 1.5,
              minHeight: 44,
              fontWeight: 700,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.875rem',
              ...(!isExpanded && {
                background: `linear-gradient(135deg, ${ACCENT} 0%, #1d4ed8 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)`,
                  boxShadow: `0 4px 12px ${ACCENT}40`,
                },
              }),
            }}
          >
            {isExpanded
              ? t('categoryGridPlus.hideSubcategories', 'Masquer')
              : t('categoryGridPlus.showSubcategories', 'Voir les sous-catégories')}
            {!isExpanded && (
              <Chip
                size="small"
                label={category.children.length}
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  pointerEvents: 'none',
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: 'inherit',
                }}
              />
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
};

ParentCard.propTypes = {
  category: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showExpand: PropTypes.bool.isRequired,
};

// ─── ChildCard ────────────────────────────────────────────────────────────────

const ChildCard = ({ category }) => {
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  const imageUrl = buildImageUrl(category.imageUrl || category.image);

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(15,23,42,0.08)',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'box-shadow .2s, border-color .2s',
        '&:hover': {
          boxShadow: `0 4px 14px ${ACCENT}22`,
          borderColor: `${ACCENT}55`,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          flexShrink: 0,
          overflow: 'hidden',
          bgcolor: `${ACCENT}08`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={getCategoryName(category)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <CategoryIcon sx={{ fontSize: 32, color: `${ACCENT}45` }} />
        )}
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="body2"
          title={getCategoryName(category)}
          sx={{
            fontWeight: 600,
            color: '#0f172a',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getCategoryName(category)}
        </Typography>
        {category.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {getCategoryDescription(category)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

ChildCard.propTypes = {
  category: PropTypes.object.isRequired,
};

// ─── CategoryGridPlus (main export) ──────────────────────────────────────────

const CategoryGridPlus = ({
  categories = [],
  showAll = false,
  columnsPerRow = 3,
}) => {
  const { t } = useTranslation('common');
  const [expandedId, setExpandedId] = useState(null);
  // Mobile breakpoint: always force 1 column below 640px
  const isMobile = useMediaQuery('(max-width: 639px)');

  const cols = isMobile ? 1 : columnsPerRow;

  // Filter to top-level parents only (parentId == null or undefined)
  const parents = categories.filter((cat) => !cat.parentId);

  // Accordion: clicking an already-open parent collapses it
  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  // Split parent list into rows of `cols` cards
  // rowIndex = Math.floor(cardIndex / cols)
  const rows = [];
  for (let i = 0; i < parents.length; i += cols) {
    rows.push(parents.slice(i, i + cols));
  }

  return (
    <Box sx={{ width: '100%' }}>
      {rows.map((row, rowIdx) => {
        // Find the expanded category within this specific row
        const expandedCat = showAll
          ? row.find((cat) => cat.id === expandedId && cat.children?.length > 0)
          : null;
        const isGroupActive = Boolean(expandedCat);

        return (
          <Box
            key={rowIdx}
            sx={{ mb: 2 }}
          >
            {/* ── Parent cards row ─────────────────────────────────── */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 2,
              }}
            >
              {row.map((cat) => (
                <ParentCard
                  key={cat.id}
                  category={cat}
                  isExpanded={showAll && cat.id === expandedId}
                  onToggle={() => toggle(cat.id)}
                  showExpand={showAll}
                />
              ))}
              {/* Phantom cells to preserve grid alignment in the last incomplete row */}
              {Array.from({ length: cols - row.length }).map((_, i) => (
                <Box key={`ph-${rowIdx}-${i}`} aria-hidden="true" />
              ))}
            </Box>

            {/* ── Children grid — injected immediately below this row ─ */}
            <AnimatePresence>
              {isGroupActive && (
                <motion.div
                  key={`children-${expandedCat.id}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2.5,
                      border: `3px solid ${ACCENT}`,
                      background: `linear-gradient(135deg, ${ACCENT}06 0%, transparent 80%)`,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: ACCENT,
                        fontWeight: 700,
                        letterSpacing: 0.8,
                        display: 'block',
                        mb: 1.5,
                      }}
                    >
                      {t('categoryGridPlus.subcategoryCount', { count: expandedCat.children.length })}
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${cols}, 1fr)`,
                        gap: 2,
                      }}
                    >
                      {expandedCat.children.map((child) => (
                        <ChildCard key={child.id} category={child} />
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        );
      })}
    </Box>
  );
};

CategoryGridPlus.propTypes = {
  /** Full flat or nested category list. Component filters to parentId == null/undefined internally. */
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      image: PropTypes.string,
      imageUrl: PropTypes.string,
      parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      children: PropTypes.array,
    })
  ),
  /** When false (default): shows parents only, no expand button. When true: enables accordion expand/collapse for children. */
  showAll: PropTypes.bool,
  /** Number of columns on desktop. Forced to 1 on mobile (< 640px). */
  columnsPerRow: PropTypes.oneOf([1, 2, 3, 4]),
};

export default CategoryGridPlus;
